import {
  DEFAULT_PAYROLL_CYCLE,
  useGetPayrollCalendarQuery,
  useUpdatePayrollCalendarMutation,
  type PayrollCalendarResponse,
  type PayrollCycleConfig,
  type PayrollCycleType,
} from '@/app/features/employee/emp-payroll';
import WithLoader from '@/components/WithLoader';
import { cyclesInMonth, resolveCycle } from '@/utils/payroll-cycle.utils';
import { DatePicker, Form, InputNumber, Radio, Select } from 'antd';
import { formatDate } from '@/utils';
import dayjs, { type Dayjs } from 'dayjs';
import CalendarIcon from '../../../Dashboard/_components/Summery/_icons/CalendarIcon';
import PanelCard from './PanelCard';
import {
  DATE_DISPLAY,
  disabledBeforeSaved,
  type ConfigPanelProps,
} from './salary-grade.types';
import { useConfigPanel } from './useConfigPanel';

type FormValues = { cycle: PayrollCycleConfig; effectiveDate: Dayjs };

const DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
  label: String(index + 1),
  value: index + 1,
}));

const CYCLE_OPTIONS: { label: string; value: PayrollCycleType }[] = [
  { label: 'Calendar Month', value: 'CALENDAR_MONTH' },
  { label: 'Custom', value: 'CUSTOM_MONTH' },
  { label: 'Fixed Interval', value: 'FIXED_INTERVAL' },
];

const PayrollCalendar = ({ payType, isEditing, ref }: ConfigPanelProps) => {
  const [form] = Form.useForm<FormValues>();
  const isDaily = payType === 'DAILY';

  // Each population has its own calendar, fetched and saved on its own.
  const { data, isLoading } = useGetPayrollCalendarQuery(payType);
  const [updatePayrollCalendar] = useUpdatePayrollCalendarMutation();

  const cycle = Form.useWatch('cycle', form);
  const cycleType = cycle?.cycle_type || 'CALENDAR_MONTH';

  // A fixed interval only makes sense for daily employees.
  const cycleOptions = isDaily
    ? CYCLE_OPTIONS
    : CYCLE_OPTIONS.filter((option) => option.value !== 'FIXED_INTERVAL');

  const toValues = (payload: PayrollCalendarResponse['data']): FormValues => ({
    cycle: payload.calendar || DEFAULT_PAYROLL_CYCLE,
    effectiveDate: dayjs(payload.calendar?.effective_date || undefined),
  });

  useConfigPanel(ref, {
    form,
    data: data?.data,
    toValues,
    isEditing,
    errorMessage: 'Payroll calendar update failed',
    submit: (values) => {
      const type = values.cycle?.cycle_type || 'CALENDAR_MONTH';

      return updatePayrollCalendar({
        payBasis: payType,
        cycleType: type,
        // The server always wants a start day. A calendar month has no such
        // setting on screen, and it always opens on the 1st.
        startOnDate:
          type === 'CALENDAR_MONTH'
            ? 1
            : Number(values.cycle?.start_on_date) || 1,
        // Only a fixed interval carries a length.
        ...(type === 'FIXED_INTERVAL'
          ? { cycleLengthDays: Number(values.cycle?.cycle_length_days) || 15 }
          : {}),
        effectiveDate: formatDate(values.effectiveDate, 'YYYY-MM-DD'),
      }).unwrap();
    },
  });

  const resolved = resolveCycle(cycle, dayjs());
  const cyclesPerMonth =
    cycleType === 'FIXED_INTERVAL' ? cyclesInMonth(cycle, dayjs()).length : 1;

  const summary = [
    { label: 'Cycle starts', value: resolved.start.format('DD MMM YYYY') },
    { label: 'Cycle ends', value: resolved.end.format('DD MMM YYYY') },
    { label: 'Total days', value: `${resolved.days} days` },
    ...(cyclesPerMonth > 1
      ? [{ label: 'Cycles this month', value: String(cyclesPerMonth) }]
      : []),
  ];

  return (
    <PanelCard title="Payroll Calendar">
      <WithLoader isLoading={isLoading} className="rounded-lg">
        <Form
          form={form}
          size="middle"
          layout="vertical"
          disabled={!isEditing || isLoading}
          className="custom-form label-gap space-y-4"
        >
          {/*
            The card is full width until 2xl and a narrow column above it, so the
            settings and the resolved period sit side by side while there is room
            and stack once there is not.
          */}
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-1 gap-4 md:gap-6">
            <div className="space-y-4">
              <Form.Item
                label="Cycle type"
                name={['cycle', 'cycle_type']}
                initialValue="CALENDAR_MONTH"
                className="!mb-0"
              >
                <Radio.Group className="readable-radio">
                  {cycleOptions.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>

              {cycleType !== 'CALENDAR_MONTH' && (
                <div className="grid grid-cols-2 gap-4 max-w-[340px]">
                  <Form.Item
                    label="Starts on day"
                    name={['cycle', 'start_on_date']}
                    initialValue={1}
                    className="disabled-bg-slate-900 !mb-0"
                    rules={[{ required: true, message: '' }]}
                  >
                    <Select options={DAY_OPTIONS} />
                  </Form.Item>

                  {cycleType === 'FIXED_INTERVAL' && (
                    <Form.Item
                      label="Cycle length"
                      name={['cycle', 'cycle_length_days']}
                      initialValue={15}
                      className="disabled-bg-slate-900 !mb-0"
                      rules={[
                        { required: true, message: '' },
                        { type: 'number', min: 1, max: 31, message: '' },
                      ]}
                    >
                      <InputNumber
                        controls={false}
                        suffix="days"
                        className="!border-transparent w-full"
                      />
                    </Form.Item>
                  )}
                </div>
              )}

              <div className="max-w-[170px]">
                <Form.Item
                  label="Effective Date"
                  name="effectiveDate"
                  className="disabled-bg-slate-900 !mb-0"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <DatePicker
                    format={DATE_DISPLAY}
                    placeholder="Select Date"
                    className="!border-transparent w-full"
                    suffixIcon={<CalendarIcon className="text-lg text-white" />}
                    disabledDate={disabledBeforeSaved(
                      data?.data?.calendar?.effective_date,
                    )}
                  />
                </Form.Item>
              </div>
            </div>

            {/* What the settings above mean for the month being viewed */}
            <div className="rounded-md border border-theme-gray-700 bg-theme-slate-900 px-4 py-3 h-fit">
              <div className="text-xs uppercase tracking-wider text-white/35 mb-3">
                {dayjs().format('MMMM YYYY')} cycle
              </div>
              <div className="divide-y divide-theme-gray-800">
                {summary.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="text-sm text-white/45">{item.label}</span>
                    <span className="text-sm font-semibold text-white">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Form>
      </WithLoader>
    </PanelCard>
  );
};

export default PayrollCalendar;
